import { Content, isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { ArrowRightIcon } from '@/assets';
import AnimatedCirclesBackgroundClient from '@/components/client-wrappers/AnimatedCirclesBackgroundClient';
import HeadlineDescriptionCtaStepsRedesign from './variants/Redesign';
import { isRedesignSlice, type RedesignVariation } from './variants/components';

export type HeadlineDescriptionCtaStepsProps =
  SliceComponentProps<Content.HeadlineDescriptionCtaStepsSlice>;

type HeadlineDescriptionCtaStepsCurrentProps = SliceComponentProps<
  Exclude<
    Content.HeadlineDescriptionCtaStepsSlice,
    { variation: RedesignVariation }
  >
>;

const HeadlineDescriptionCtaSteps = ({
  slice,
  ...rest
}: HeadlineDescriptionCtaStepsProps): JSX.Element => {
  if (isRedesignSlice(slice)) {
    return <HeadlineDescriptionCtaStepsRedesign slice={slice} {...rest} />;
  }
  return <HeadlineDescriptionCtaStepsCurrent slice={slice} {...rest} />;
};

const HeadlineDescriptionCtaStepsCurrent = ({
  slice,
}: HeadlineDescriptionCtaStepsCurrentProps): JSX.Element => {
  // Two-column dark layout: kicker + headline on the left, a numbered list of
  // steps (01/02/03) on the right, each with its own outlined CTA button.
  if (slice.variation === 'numberedWithCtas') {
    const primary = slice.primary as unknown as {
      kicker?: string;
      background_image?: import('@prismicio/client').ImageField;
      headline: Content.HeadlineDescriptionCtaStepsSliceDefault['primary']['headline'];
      summary: Content.HeadlineDescriptionCtaStepsSliceDefault['primary']['summary'];
      steps: Array<{
        step_label?: string;
        step_title: Content.HeadlineDescriptionCtaStepsSliceDefaultPrimaryStepsItem['step_title'];
        step_description: Content.HeadlineDescriptionCtaStepsSliceDefaultPrimaryStepsItem['step_description'];
        step_cta?: import('@prismicio/client').LinkField;
      }>;
    };
    const { kicker, background_image, headline, summary, steps } = primary;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative mx-5 rounded-[24px] bg-[#01060a] text-white md:mx-[30px]"
      >
        {/* Background layer — clipped to the rounded corners. Kept separate from
            the content so no `overflow-hidden` ancestor wraps the sticky title
            (overflow:hidden on an ancestor breaks position: sticky). */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[24px]">
          {isFilled.image(background_image) ? (
            <>
              <PrismicNextImage
                field={background_image}
                fill
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover object-center"
                fallbackAlt=""
              />
              <div className="absolute inset-0 bg-[#01060a]/70" />
            </>
          ) : (
            <AnimatedCirclesBackgroundClient />
          )}
        </div>
        <div className="relative mx-auto max-w-[1140px] px-5 py-20 md:px-8 md:py-28">
          {/* Kicker pill + horizontal rule */}
          <div className="flex items-center gap-5">
            {kicker && (
              <span className="shrink-0 rounded-full border border-[#00F557] px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-white md:text-[12px]">
                {kicker}
              </span>
            )}
            <div className="h-px flex-1 bg-white/15" />
          </div>

          <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left: headline pinned (sticky) while the numbered steps scroll
                on the right — mirrors FeatureComparisonGrid's intro header.
                Offset tracks the fixed nav (top = announcement bar + 28px, 72px
                tall) so the menu never overlaps the title on scroll. */}
            <div className="lg:sticky lg:top-[calc(var(--announcement-bar-height,0px)_+_120px)] lg:self-start">
              <PrismicRichText
                field={headline}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="max-w-[460px] text-4xl font-semibold leading-[1.15] md:text-5xl">
                      {children}
                    </h2>
                  ),
                }}
              />
              {isFilled.richText(summary) && (
                <div className="mt-6 max-w-[440px]">
                  <PrismicRichText
                    field={summary}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-lg text-white/80">{children}</p>
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            {/* Right: numbered steps */}
            <div className="flex flex-col gap-14">
              {steps.map((item, index) => (
                <div key={index} className="flex flex-col">
                  <span className="text-[56px] font-bold leading-none text-white md:text-[72px]">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {item.step_label && (
                    <p className="mt-4 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#00F557]">
                      {item.step_label}
                    </p>
                  )}

                  <PrismicRichText
                    field={item.step_title}
                    components={{
                      heading3: ({ children }) => (
                        <h3 className="mt-2 text-[22px] font-semibold text-white md:text-2xl">
                          {children}
                        </h3>
                      ),
                    }}
                  />

                  <PrismicRichText
                    field={item.step_description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="mt-4 max-w-[520px] text-[15px] leading-[1.6] text-white/70">
                          {children}
                        </p>
                      ),
                    }}
                  />

                  {isFilled.link(item.step_cta) && (
                    <div className="mt-6">
                      <PrismicNextLink
                        field={item.step_cta}
                        className="group inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:border-white hover:bg-white/5"
                      >
                        <ArrowRightIcon className="h-4 w-4" />
                        {(item.step_cta as { text?: string }).text}
                      </PrismicNextLink>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Inset dark card: kicker + rule, centered headline, and a row of bordered
  // cards each with a green icon, title and description.
  if (slice.variation === 'differenceCards') {
    const primary = slice.primary as unknown as {
      kicker?: string;
      background_image?: import('@prismicio/client').ImageField;
      headline: Content.HeadlineDescriptionCtaStepsSliceDefault['primary']['headline'];
      cards?: Array<{
        icon?: import('@prismicio/client').ImageField;
        card_title?: string;
        card_description?: string;
      }>;
    };
    const { kicker, background_image, headline, cards = [] } = primary;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#01060a] text-white"
      >
        {isFilled.image(background_image) ? (
          <>
            <PrismicNextImage
              field={background_image}
              fill
              sizes="100vw"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
              fallbackAlt=""
            />
            <div className="absolute inset-0 bg-[#01060a]/70" />
          </>
        ) : (
          <AnimatedCirclesBackgroundClient />
        )}
        <div className="relative mx-auto max-w-[1140px] px-5 py-20 md:px-8 md:py-28">
          {/* Kicker pill + horizontal rule */}
          <div className="flex items-center justify-center gap-5 md:justify-start">
            {kicker && (
              <span className="shrink-0 rounded-full border border-[#00F557] px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-white md:text-[12px]">
                {kicker}
              </span>
            )}
            {/* Divider line hidden on mobile so the eyebrow reads centered. */}
            <div className="hidden h-px flex-1 bg-white/15 md:block" />
          </div>

          {isFilled.richText(headline) && (
            <div className="mt-10 text-center">
              <PrismicRichText
                field={headline}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="text-balance text-3xl font-semibold leading-[1.15] md:text-[40px]">
                      {children}
                    </h2>
                  ),
                }}
              />
            </div>
          )}

          {cards.length > 0 && (
            <div className="mt-12 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-3">
              {cards.map((card, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center gap-4 rounded-[24px] border border-white/10 bg-white/[0.03] px-6 py-10 text-center"
                >
                  {isFilled.image(card.icon) && (
                    <PrismicNextImage
                      field={card.icon}
                      className="h-12 w-auto object-contain"
                      fallbackAlt=""
                    />
                  )}
                  {card.card_title && (
                    <h3 className="text-[20px] font-semibold leading-[1.3] text-white">
                      {card.card_title}
                    </h3>
                  )}
                  {card.card_description && (
                    <p className="text-[14px] font-light leading-[1.6] text-white/70">
                      {card.card_description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  // Separate handling for the two variations to fix TypeScript errors
  if (slice.variation === 'stepsWithIcon') {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-white py-20 md:py-32 overflow-hidden"
      >
        <AnimatedCirclesBackgroundClient />
        <div className="relative container px-4 md:px-6">
          <div
            id="headline-and-cta"
            className="max-w-3xl text-start flex flex-col items-start gap-6"
          >
            <PrismicRichText
              field={slice.primary.headline}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-4xl md:text-5xl font-bold">{children}</h2>
                ),
              }}
            />
            <PrismicRichText
              field={slice.primary.summary}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg text-white/80">{children}</p>
                ),
              }}
            />
            <PrismicNextLink
              field={slice.primary.primary_cta}
              className="secondary-button"
            >
              <ArrowRightIcon className="arrow-icon" />
              Discover how VelX drives outcomes
            </PrismicNextLink>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {slice.primary.steps.map((item, index) => (
              <div
                key={index}
                className={`h-full flex flex-col rounded-2xl p-8  ${
                  index === 1 ? 'md:mt-8' : index === 2 ? 'md:mt-16' : ''
                }`}
                style={{
                  background:
                    'linear-gradient(to bottom, rgba(0, 0, 0, 0.1875), rgba(0, 0, 0, 0))',
                  boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.08)',
                }}
              >
                {isFilled.image(item.step_icon) && (
                  <PrismicNextImage
                    field={item.step_icon}
                    className="h-8 w-8 mb-4"
                  />
                )}
                <PrismicRichText
                  field={item.step_title}
                  components={{
                    heading3: ({ children }) => (
                      <h3 className="text-2xl font-semibold text-white mt-2">
                        {children}
                      </h3>
                    ),
                  }}
                />
                <PrismicRichText
                  field={item.step_description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-white/80 mt-4">{children}</p>
                    ),
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Default variation
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-white md:pb-24 bg-[#0F423A]"
    >
      <div className="absolute top-0 left-0 w-full h-52 md:h-52 z-20 bg-gradient-to-b from-[#0E1010] to-transparent pointer-events-none" />
      <div className="container mx-auto px-4 md:px-6">
        <div
          id="headline-and-cta"
          className="max-w-5xl text-start flex flex-col items-start gap-6"
        >
          <PrismicRichText
            field={slice.primary.headline}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-4xl md:text-5xl font-bold mt-20 z-20">
                  {children}
                </h2>
              ),
            }}
          />
          <PrismicRichText
            field={slice.primary.summary}
            components={{
              paragraph: ({ children }) => (
                <p className="text-lg text-white/80">{children}</p>
              ),
            }}
          />
          <PrismicNextLink
            field={slice.primary.primary_cta}
            className="secondary-button"
          >
            <ArrowRightIcon className="arrow-icon" />
            Discover how VelX drives outcomes
          </PrismicNextLink>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {slice.primary.steps.map((item, index) => (
            <div
              key={index}
              className={`h-full flex flex-col rounded-2xl p-8 ${
                index === 1 ? 'md:mt-8' : index === 2 ? 'md:mt-16' : ''
              }`}
              style={{
                background:
                  'linear-gradient(to bottom, rgba(0, 0, 0, 0.1875), rgba(0, 0, 0, 0))',
                boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <p className="text-sm text-white/60 uppercase">
                {item.step_label}
              </p>
              <PrismicRichText
                field={item.step_title}
                components={{
                  heading3: ({ children }) => (
                    <h3 className="text-2xl font-semibold text-green-400 mt-2">
                      {children}
                    </h3>
                  ),
                }}
              />
              <PrismicRichText
                field={item.step_description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-white/80 mt-4">{children}</p>
                  ),
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeadlineDescriptionCtaSteps;
